import {
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useTellescopeQueries,
  useTellescopeQuery,
  useUsersMap,
} from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { MessageCircle } from "lucide-react";
import { useMemo } from "react";
import { ReversePaginatedList } from "@tellescope/ui";
import { formatName } from "@tellescope/utils";
import { MessageBubbleWithSecureAvatar } from "../components/MessageBubbleWithSecureAvatar";

export interface ChatRoomContextCardProps {
  chatRoomId: string;
}

export const ChatRoomContextCard = ({ chatRoomId }: ChatRoomContextCardProps) => {
  const { t } = useTranslation("tickets");

  // Get the chat room
  const chatRoomQuery = useTellescopeQuery({
    model: "chat_rooms",
    method: "getOne",
    request: chatRoomId,
    enabled: !!chatRoomId,
  });

  // Get chat messages for this room
  const messagesQuery = useTellescopeInfiniteQuery({
    model: "chats",
    method: "getSome",
    request: {
      filter: {
        roomId: chatRoomId,
      },
      limit: 25,
    },
    enabled: !!chatRoomId,
    select: (data) => data.pages.flat().reverse(),
  });

  // Get unique enduser IDs from messages
  const enduserIds = useMemo(() => {
    const messages = messagesQuery.data ?? [];
    const ids = new Set<string>();
    messages.forEach((msg) => {
      if (msg.enduserId) ids.add(msg.enduserId);
    });
    return Array.from(ids);
  }, [messagesQuery.data]);

  const userMap = useUsersMap();

  // Query endusers
  const endusersMap = useTellescopeQueries({
    model: "endusers",
    method: "getOne",
    requests: enduserIds,
    combine: (queries) =>
      queries.reduce<Record<string, TellescopeModelFromName<"endusers">>>((acc, curr) => {
        if (curr.data) acc[curr.data.id] = curr.data;
        return acc;
      }, {}),
    enabled: enduserIds.length > 0,
  });

  if (chatRoomQuery.isLoading || messagesQuery.isLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p className="text-sm">{t("Loading chat...")}</p>
      </div>
    );
  }

  if (chatRoomQuery.isError || messagesQuery.isError || !chatRoomQuery.data) {
    return (
      <div className="p-4 border border-destructive rounded-lg">
        <p className="text-sm text-destructive">{t("Unable to load chat. Please try again.")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 p-4 border border-gray-200 rounded-lg bg-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <MessageCircle className="h-4 w-4 text-gray-600 flex-shrink-0" />
          <h3 className="text-sm font-medium text-gray-900 truncate">{t("Chat")}</h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
          <span>
            {t("{{count}} message", {
              count: chatRoomQuery.data.numMessages ?? messagesQuery.data?.length ?? 0,
              defaultValue_one: "{{count}} message",
              defaultValue_other: "{{count}} messages",
            })}
          </span>
        </div>
      </div>

      {chatRoomQuery.data.title && (
        <div className="text-sm text-gray-700 font-medium px-3 py-2 bg-gray-50 rounded border border-gray-200">
          <span className="text-xs text-gray-500 mr-2">{t("Title:")}</span>
          {chatRoomQuery.data.title}
        </div>
      )}

      <div className="overflow-hidden border border-gray-200 rounded-lg max-h-[400px]">
        <ReversePaginatedList className="max-h-[400px] p-4" onTopReached={() => messagesQuery.fetchNextPage()}>
          {(messagesQuery.data ?? []).map((message) => {
            const isFromEnduser = !!message.enduserId && !message.userId;
            const sender = isFromEnduser
              ? endusersMap[message.enduserId ?? ""]
              : userMap[message.senderId || message.userId || message.creator];

            return (
              <MessageBubbleWithSecureAvatar
                key={message.id}
                content={message.message}
                timestamp={message.createdAt}
                isOutbound={!isFromEnduser}
                sender={
                  sender
                    ? {
                        id: sender.id,
                        type: isFromEnduser ? "enduser" : "user",
                        name: formatName(sender),
                        avatar: sender.avatar,
                      }
                    : {
                        id: "",
                        type: "user",
                        name: t("Unknown"),
                      }
                }
                chatAttachments={message.attachments}
              />
            );
          })}
        </ReversePaginatedList>
      </div>
    </div>
  );
};
